import {
  type ConfigurationPackage,
  OpenAPI as ConfigurationOpenAPI,
} from '@festina/configuration-general-types/configuration'
import type { LayerKey } from '@festina-life/api'
import {
  formatIssues,
  isValidationErrorMessage,
} from '@festina-life/components'
import type { ConfigurableServer, Optional } from '@festina-life/core'
import { useMutation } from '@tanstack/react-query'

type TriggerProps = {
  csvFile: File
  server: ConfigurableServer
  branch: string
  layer: LayerKey
  block: string
}

const SHARED_TENANT_NAME_ON_SERVER = 'shared'

export const useMutateConvertParameterValuesCsvToConfigurationPackage = () =>
  useMutation({
    mutationFn: async ({
      csvFile,
      server,
      branch,
      layer,
      block,
    }: TriggerProps) => {
      if (!ConfigurationOpenAPI.BASE) {
        throw new Error('The configuration service has not been configured')
      }

      const conversionUrl = layer.layerId
        ? `${ConfigurationOpenAPI.BASE}/conversion/${encodeURIComponent(server.toLocaleLowerCase())}/${encodeURIComponent(branch)}/parameters/${block}/${encodeURIComponent(layer.tenantId ?? SHARED_TENANT_NAME_ON_SERVER)}/${encodeURIComponent(layer.layerId)}/convert-to-package`
        : `${ConfigurationOpenAPI.BASE}/conversion/${encodeURIComponent(server.toLocaleLowerCase())}/${encodeURIComponent(branch)}/parameters/${block}/${encodeURIComponent(layer.tenantId ?? SHARED_TENANT_NAME_ON_SERVER)}/convert-to-package`
      const serverToken = ConfigurationOpenAPI.TOKEN

      const headers = new Headers()

      if (serverToken) {
        const token =
          typeof serverToken === 'string'
            ? serverToken
            : await serverToken({
                method: 'POST',
                url: conversionUrl,
              })

        headers.append('authorization', `Bearer ${token}`)

        headers.append('accept', 'application/json')
        headers.append('Content-Type', 'application/octet-stream')
      }

      const request: RequestInit = {
        headers,
        body: csvFile,
        method: 'POST',
      }

      const response = await fetch(conversionUrl, request)

      if (!response.ok) {
        const json = await response.json()

        throw new Error(
          `Failed to publish test sheet: ${isValidationErrorMessage(json) ? formatIssues(json) : response.statusText}`,
        )
      }

      return (await response.json()) as Optional<ConfigurationPackage>
    },
  })
